import { err, extractErrorDetails, ok } from '@src/services/common/Result';
import { isTempClipId } from '@src/services/WriteQueueService';

import type { Result } from '@src/services/common/Result';

/**
 * PLAT-11929 — leaving Creator Studio without losing the last edits.
 *
 * Creator Studio saves on Save, or on an occasional autosave that runs just
 * BEFORE a command and so captures the state without it. Navigating away
 * straight after an edit therefore dropped that edit: the Content Studio clip
 * preview reopened on the last saved version (a Voice set to Both, a moved
 * caption — anything since the last save).
 *
 * Two halves, both pure: `nextHasUnsavedEdits` decides, from the store's save
 * and undo/redo state, whether anything has changed since the last save
 * started; `saveBeforeLeaving` waits out a save in flight and, only if there
 * is something to keep, runs one more and reports whether it landed.
 */

/** What the edit tracker reads off the store between two moments. */
export interface EditorSaveState {
  videoId: string | null;
  /** A background save is in flight. */
  saving: boolean;
  /** The last background save failed. */
  failed: boolean;
  /** The newest undo / redo entries; a new entry is a new edit (or an undo). */
  undoTop: unknown;
  redoTop: unknown;
}

/**
 * Whether there are edits no save has taken yet, after the store moved from
 * `prev` to `next`.
 *
 * Every user edit goes through the editor's patched `executeCommand`, which
 * pushes an undo entry; Undo and Redo move an entry between the stacks. So a
 * change at either stack's top is an edit. Both tops emptying at once is a
 * history reset (a video loading), not an edit.
 *
 * Order matters when a save starts and an edit lands in the same change: the
 * autosave fires inside `executeCommand` before the command runs, so the save
 * holds the state WITHOUT that edit. The save's start is applied first and
 * the edit after it, which leaves the edit counted as unsaved.
 */
export function nextHasUnsavedEdits(
  hasUnsavedEdits: boolean,
  prev: EditorSaveState,
  next: EditorSaveState,
): boolean {
  // A different video is a fresh start; its history was reset on load. A
  // temporary (or missing) id becoming the real one is the same video, saved
  // for the first time.
  const idAssigned = prev.videoId === null || isTempClipId(prev.videoId);
  if (next.videoId !== prev.videoId && !idAssigned) return false;

  let unsaved = hasUnsavedEdits;
  // A save that starts takes every edit made so far…
  if (next.saving && !prev.saving) unsaved = false;
  // …unless it fails, in which case none of them landed.
  if (!next.saving && prev.saving && next.failed) unsaved = true;

  const historyMoved =
    next.undoTop !== prev.undoTop || next.redoTop !== prev.redoTop;
  const historyReset = next.undoTop === undefined && next.redoTop === undefined;
  if (historyMoved && !historyReset) unsaved = true;

  return unsaved;
}

/** The save surface `saveBeforeLeaving` needs, and no more. */
export interface SaveBeforeLeavingDeps {
  readonly isBackgroundSaving: boolean;
  readonly savingError?: unknown;
  waitForBackgroundSave(): Promise<void>;
  saveStoryAndVideo(
    asFinal?: boolean,
    withRenderer?: boolean,
    resetTimeline?: boolean,
    autoSave?: boolean,
  ): Promise<void>;
}

export interface SaveBeforeLeavingResult {
  /** A save ran because there were unsaved edits. */
  saved: boolean;
}

/**
 * Make sure the loaded video's edits are saved before Creator Studio is left.
 *
 * Saves already in flight are waited out first: a save requested during one is
 * only queued, and the store's wait resolves when the CURRENT one ends, so
 * awaiting a queued save would return before it ran. With nothing unsaved it
 * returns without saving, so merely viewing a clip writes no new version and
 * leaves a New clip New.
 *
 * Fails when the save does not land. The store has already raised its own
 * retry prompt by then; the caller should stay put so the edits are not lost.
 */
export async function saveBeforeLeaving(
  deps: SaveBeforeLeavingDeps,
  hasUnsavedEdits: () => boolean,
): Promise<Result<SaveBeforeLeavingResult>> {
  try {
    // A loop, not one wait: a save queued during the one in flight starts as
    // that one ends, and the tracker already counts it as taking the edits.
    while (deps.isBackgroundSaving) {
      // A failure here is the store's to report; whether anything is still
      // unsaved is read below either way.
      await deps.waitForBackgroundSave().catch(() => undefined);
    }

    if (!hasUnsavedEdits()) return ok({ saved: false });

    // An autosave: it records as one in the video's history, and the
    // renderer is kept because the person may come back to this video.
    await deps.saveStoryAndVideo(false, true, false, true);

    // `saveStoryAndVideo` swallows a failed background save and leaves the
    // error on the store, so success has to be read back from there.
    if (deps.savingError) {
      return err('EXTERNAL', 'The video could not be saved before leaving');
    }
    return ok({ saved: true });
  } catch (e) {
    return err(
      'UNKNOWN',
      'The video could not be saved before leaving',
      extractErrorDetails(e),
    );
  }
}
